import {
  Box,
  Button,
  Checkbox,
  DialogContent,
  FormControlLabel,
  FormHelperText,
  Stack,
} from "@mui/material";
import { FormikHelpers, getIn, useFormik } from "formik";
import { omit } from "lodash-es";
import { useCallback, useEffect, useState } from "react";

import useCascadeActionSpec from "@/common/hooks/entity/cascadeActions/useCascadeActionSpec";
import useMounted from "@/common/hooks/mount/useMounted";
import { EntityAffiliationInfo } from "@/common/ts/entity";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import { CascadeActionInputDto, CascadeActionType, EntityType } from "@/core/api/generated/v1";

import AppIconButton from "../../Button/AppIconButton";
import RelatedEntityTypesInputModal from "../../CascadeActions/RelatedEntityTypesInputModal";
import AppIcon from "../../Icons/AppIcon";
import AppModal, { AppModalProps } from "../../Modals/AppModal";
import AppModalTitle from "../../Modals/AppModalTitle";
import EntityAffiliationInputV2 from "./EntityAffiliationInputV2";

export type CascadeActionFormikValues = CascadeActionInputDto & { submit: string };

export interface EntityAffiliationUpdateCascadeModalProps extends Omit<AppModalProps, "children"> {
  entityType: EntityType;
  entityId: Nil<string>;
  affiliationInfo: Nil<EntityAffiliationInfo>;
  onUpdate: () => void;
}

export default function EntityAffiliationUpdateCascadeModal({
  entityType,
  entityId,
  affiliationInfo,
  onUpdate,
  ...modalProps
}: EntityAffiliationUpdateCascadeModalProps) {
  const mounted = useMounted();

  const [isUpdateCascade, setIsUpdateCascade] = useState(false);
  const [isUpdateCascadeSettingsOpen, setIsUpdateCascadeSettingsOpen] = useState(false);

  const cascadeActionSpec = useCascadeActionSpec(
    entityType,
    CascadeActionType.UpdateEntityAffiliation,
  );

  const getInitialValues = () => {
    return {
      baseEntity: {
        entityType: entityType,
        entityId: entityId || undefined,
      },
      relatedEntitiesTypes: undefined,
      parameters: {
        updateEntityAffiliation: {
          departmentIds: affiliationInfo?.departmentIds || [],
          locationIds: affiliationInfo?.locationIds || [],
        },
      },
      submit: "",
    };
  };

  const resetValues = (params: {
    setValues: FormikHelpers<CascadeActionFormikValues>["setValues"];
  }) => {
    params.setValues(getInitialValues());
  };

  const formik = useFormik<CascadeActionFormikValues>({
    enableReinitialize: true,
    initialValues: {
      ...getInitialValues(),
    },
    onSubmit: async (values, { setFieldError, setStatus, setSubmitting, setValues }) => {
      try {
        if (mounted.current) {
          setSubmitting(true);
          await apiClient.cascadeActionsApi.apiV1CascadeActionsUpdateAffiliationPost({
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            cascadeActionInputDto: {
              ...omit(values, "submit"),
              relatedEntitiesTypes: isUpdateCascade ? values.relatedEntitiesTypes : [],
            },
          });

          setStatus({ success: true });
          setSubmitting(false);
          onUpdate && onUpdate();
          resetValues({ setValues });
          modalProps?.onClose && modalProps?.onClose({}, "escapeKeyDown");
        }
      } catch (err: any) {
        if (mounted.current) {
          ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
          setStatus({ success: false });
          setSubmitting(false);
        }
      }
    },
  });

  const { values, errors, setFieldValue, setValues, isSubmitting, handleSubmit } = formik;

  useEffect(() => {
    if (cascadeActionSpec) {
      setFieldValue(
        "relatedEntitiesTypes",
        isUpdateCascade ? cascadeActionSpec.relatedEntitiesTypes : undefined,
      );
    }
  }, [cascadeActionSpec, isUpdateCascade]);

  const handleClose = useCallback(() => {
    resetValues({ setValues });
    setIsUpdateCascade(false);
    modalProps.onClose && modalProps.onClose({}, "escapeKeyDown");
  }, [entityType, entityId, setValues]);

  return (
    <AppModal
      fullWidth
      maxWidth='md'
      keepMounted={false}
      {...{ ...modalProps, onClose: handleClose }}
    >
      <AppModalTitle title='Update entity affiliation' withClose onClose={() => handleClose()} />
      <DialogContent>
        <Stack spacing={2}>
          {/* Entity affiliation */}
          <EntityAffiliationInputV2
            departments={{
              departmentIds: values.parameters?.updateEntityAffiliation?.departmentIds,
              onChange: (departmentIds) => {
                setFieldValue("parameters.updateEntityAffiliation.departmentIds", departmentIds);
              },
              error: getIn(errors, "parameters.updateEntityAffiliation.departmentIds"),
            }}
            locations={{
              locationIds: values.parameters?.updateEntityAffiliation?.locationIds,
              onChange: (locationIds) => {
                setFieldValue("parameters.updateEntityAffiliation.locationIds", locationIds);
              },
              error: getIn(errors, "parameters.updateEntityAffiliation.locationIds"),
            }}
          />

          <Stack direction='row' spacing={1} alignItems='center'>
            <Box>
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Checkbox
                    checked={isUpdateCascade ?? false}
                    onChange={(e) => {
                      const isCascade2 = e.target.checked;
                      setIsUpdateCascade(isCascade2);
                    }}
                  />
                }
                label='Update affiliation cascade'
              />
            </Box>

            {isUpdateCascade && (
              <AppIconButton size='small' onClick={() => setIsUpdateCascadeSettingsOpen(true)}>
                <AppIcon of='settings' />
              </AppIconButton>
            )}
          </Stack>

          <FormHelperText error={true}>{errors.submit}</FormHelperText>

          <RelatedEntityTypesInputModal
            open={isUpdateCascadeSettingsOpen}
            onClose={() => setIsUpdateCascadeSettingsOpen(false)}
            spec={cascadeActionSpec}
            relatedEntitiesTypes={values.relatedEntitiesTypes || undefined}
            onRelatedTypesChange={(newValue) => setFieldValue("relatedEntitiesTypes", newValue)}
          />

          <Button
            loading={isSubmitting}
            disabled={!entityId}
            color='primary'
            fullWidth
            type='submit'
            variant='contained'
            onClick={() => handleSubmit()}
            sx={{ mt: { xs: "auto", md: 2 }, mb: 2 }}
          >
            Save
          </Button>
        </Stack>
      </DialogContent>
    </AppModal>
  );
}
